import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/dpa')({
  component: DpaPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function DpaPage() {
  return (
    <div>
      <Helmet>
        <title>Data Processing Agreement | One-Email</title>
        <meta name="description" content="One-Email Data Processing Agreement (DPA). GDPR-compliant data processing terms for email infrastructure, sub-processors, security measures, and international transfers." />
        <link rel="canonical" href="https://one-email.com/legal/dpa" />
      </Helmet>

      <h1 style={s.heading}>Data Processing Agreement</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> When you use One-Email to send emails, we process personal data (email addresses, metadata) on your behalf. You are the data controller &mdash; you decide who receives emails and what they contain. We are the data processor &mdash; we deliver them according to your instructions. This DPA explains how we handle that data in compliance with GDPR.
      </div>

      {/* 1. Definitions */}
      <h2 id="definitions" style={s.h2}>1. Definitions</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>&ldquo;Controller&rdquo;</strong> means you, the customer who uses One-Email to send emails. You determine the purposes and means of processing personal data.</li>
        <li style={s.li}><strong>&ldquo;Processor&rdquo;</strong> means One-Email (operated by IMP Capital). We process personal data solely on your documented instructions.</li>
        <li style={s.li}><strong>&ldquo;Personal Data&rdquo;</strong> means any information relating to an identified or identifiable natural person, processed through the Service. This includes email addresses, IP addresses, and any personal data contained in tags you attach to emails.</li>
        <li style={s.li}><strong>&ldquo;Sub-processor&rdquo;</strong> means a third party engaged by One-Email to process Personal Data on the Controller&apos;s behalf.</li>
        <li style={s.li}><strong>&ldquo;Data Subject&rdquo;</strong> means the identifiable natural person to whom Personal Data relates &mdash; in this context, the recipients of emails you send through One-Email.</li>
        <li style={s.li}><strong>&ldquo;Applicable Data Protection Law&rdquo;</strong> means GDPR (Regulation (EU) 2016/679), UK GDPR (Data Protection Act 2018), and any other applicable data protection legislation.</li>
        <li style={s.li}><strong>&ldquo;SCCs&rdquo;</strong> means the EU Commission&apos;s Standard Contractual Clauses for international data transfers (Commission Implementing Decision (EU) 2021/914).</li>
      </ul>

      {/* 2. Scope of Processing */}
      <h2 id="scope" style={s.h2}>2. Scope of Processing</h2>
      <p style={s.p}>This DPA applies to all processing of Personal Data that One-Email performs on your behalf via the Service.</p>

      <h3 style={s.h3}>Subject Matter</h3>
      <p style={s.p}>
        Email delivery infrastructure services &mdash; receiving email payloads via API, delivering via SMTP, storing metadata for analytics and status tracking.
      </p>

      <h3 style={s.h3}>Duration</h3>
      <p style={s.p}>
        The duration of your use of the Service, plus 30 days post-termination (grace period for data export), plus any additional period required by law.
      </p>

      <h3 style={s.h3}>Nature and Purpose of Processing</h3>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}>Receiving email send requests via <code>POST /v1/emails</code> and <code>POST /v1/emails/batch</code></li>
        <li style={s.li}>Composing RFC 5322 compliant email messages with DKIM signing</li>
        <li style={s.li}>Delivering emails to recipient mail servers via SMTP</li>
        <li style={s.li}>Storing email metadata (sender, recipient, status, timestamps, tags) for your tier&apos;s retention period</li>
        <li style={s.li}>Processing delivery events (bounces, complaints, opens, clicks) and making them available via API and webhooks</li>
        <li style={s.li}>Maintaining suppression lists based on hard bounces and complaints</li>
        <li style={s.li}>Enforcing sending limits and abuse prevention measures to protect shared infrastructure</li>
      </ol>

      <h3 style={s.h3}>Types of Personal Data Processed</h3>
      <ul style={s.ul}>
        <li style={s.li}>Email addresses (sender and recipient)</li>
        <li style={s.li}>Subject line (stored as SHA-256 hash only)</li>
        <li style={s.li}>Delivery metadata (timestamps, status, transport, sending IP)</li>
        <li style={s.li}>Tags (customer-defined key-value pairs that may contain personal data)</li>
        <li style={s.li}>SMTP message IDs</li>
      </ul>

      <h3 style={s.h3}>Categories of Data Subjects</h3>
      <p style={s.p}>
        Recipients of emails you send through One-Email &mdash; your customers, users, subscribers, or other individuals you email.
      </p>

      {/* 3. Controller Obligations and Rights */}
      <h2 id="controller" style={s.h2}>3. Controller Obligations and Rights</h2>
      <p style={s.p}>You (the Controller) are responsible for:</p>
      <ul style={s.ul}>
        <li style={s.li}>Ensuring you have a lawful basis (under GDPR Article 6 or equivalent) to email the recipients whose data you provide to One-Email</li>
        <li style={s.li}>Complying with all applicable data protection laws in your use of the Service</li>
        <li style={s.li}>Providing instructions to One-Email solely through the API and dashboard (these constitute your documented instructions)</li>
        <li style={s.li}>Responding to data subject requests from your email recipients</li>
        <li style={s.li}>Conducting Data Protection Impact Assessments where required</li>
      </ul>

      {/* 4. Processor Obligations */}
      <h2 id="processor" style={s.h2}>4. Processor Obligations</h2>

      <h3 style={s.h3}>4.1 Instructions-Only Processing (Article 28(3)(a))</h3>
      <p style={s.p}>
        One-Email processes Personal Data <strong>only on your documented instructions</strong>. Your API calls and dashboard actions constitute your documented instructions. We will not process Personal Data for any other purpose.
      </p>
      <p style={s.p}>
        If we are required to process Personal Data by EU or Member State law, we will inform you before processing (unless the law prohibits such notification).
      </p>
      <p style={s.p}>
        If we believe any instruction from you infringes GDPR or other Applicable Data Protection Law, we will <strong>immediately inform you</strong> (Article 28(3) final sentence).
      </p>

      <h3 style={s.h3}>4.2 Confidentiality (Article 28(3)(b))</h3>
      <p style={s.p}>
        All persons authorized to process Personal Data on our behalf have committed to confidentiality or are under an appropriate statutory obligation of confidentiality.
      </p>

      <h3 style={s.h3}>4.3 Security Measures (Article 28(3)(c) / Article 32)</h3>
      <p style={s.p}>
        We implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:
      </p>

      <p style={s.p}><strong>Encryption:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>All data encrypted at rest (Neon Postgres encryption, Scaleway disk encryption, Cloudflare R2 encryption)</li>
        <li style={s.li}>All data encrypted in transit (TLS 1.3 for API and dashboard, TLS for SMTP delivery where supported by receiving server, MTA-STS enforcement)</li>
      </ul>

      <p style={s.p}><strong>Access Controls:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>API keys hashed with SHA-256 before storage &mdash; plaintext keys are never stored</li>
        <li style={s.li}>DKIM private keys encrypted using envelope encryption (AES-256-GCM with Key Encryption Key stored in Cloudflare Worker secrets, separate from the database)</li>
        <li style={s.li}>Cloudflare Worker secrets for all sensitive configuration (never in source control)</li>
      </ul>

      <p style={s.p}><strong>Data Minimization:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>Email body content is <strong>not retained</strong> after delivery &mdash; processed in memory and discarded</li>
        <li style={s.li}>Attachments deleted from storage immediately after send confirmation</li>
        <li style={s.li}>Subject lines stored as SHA-256 hashes only (not human-readable)</li>
      </ul>

      <p style={s.p}><strong>Monitoring:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>4-layer abuse enforcement system (ramp limits, automated metric monitoring, proactive signal detection, manual review)</li>
        <li style={s.li}>Automated pause on breach of bounce/complaint thresholds</li>
        <li style={s.li}>Abuse monitoring cron running every 5 minutes</li>
      </ul>

      <p style={s.p}><strong>Infrastructure:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>SMTP servers accept connections only from authorized sources</li>
        <li style={s.li}>All servers hardened with principle of least privilege</li>
        <li style={s.li}>No shared hosting &mdash; dedicated infrastructure for email delivery</li>
      </ul>

      <h3 style={s.h3}>4.4 Sub-processors (Article 28(3)(d))</h3>
      <p style={s.p}>
        We will not engage a new sub-processor without your prior <strong>general written authorization</strong>, which you grant by accepting this DPA. We will:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Notify you at least 30 days</strong> before adding a new sub-processor, via email to your account&apos;s registered address and via notice on our <a href="/legal/subprocessors">sub-processor page</a></li>
        <li style={s.li}>Give you the opportunity to <strong>object</strong> to a new sub-processor within 30 days</li>
        <li style={s.li}>If your objection cannot be reasonably resolved, you may terminate the Agreement</li>
        <li style={s.li}>Ensure all sub-processors are bound by data protection obligations <strong>no less protective</strong> than those in this DPA</li>
      </ul>
      <p style={s.p}>
        <strong>Sub-processor liability (Article 28(4)):</strong> Where a sub-processor fails to fulfil its data protection obligations, One-Email remains <strong>fully liable</strong> to you for the performance of that sub-processor&apos;s obligations.
      </p>
      <p style={s.p}>
        Our current sub-processors are listed at <a href="/legal/subprocessors">/legal/subprocessors</a>.
      </p>

      <h3 style={s.h3}>4.5 Assistance with Data Subject Rights (Article 28(3)(e))</h3>
      <p style={s.p}>
        We will assist you in responding to data subject requests (access, rectification, erasure, portability, restriction, objection) to the extent technically feasible.
      </p>
      <p style={s.p}>Assistance mechanisms available to you:</p>
      <ul style={s.ul}>
        <li style={s.li}><code>GET /v1/emails</code> &mdash; look up metadata for specific recipients</li>
        <li style={s.li}>Suppression list API &mdash; add or remove addresses</li>
        <li style={s.li}><code>DELETE /v1/account/data</code> &mdash; full account data deletion</li>
        <li style={s.li}>Email privacy@one-email.com for manual assistance</li>
      </ul>
      <p style={s.p}>
        We will not independently respond to data subject requests about recipient data without your instruction. If a data subject contacts us directly about emails sent by you, we will direct them to you.
      </p>

      <h3 style={s.h3}>4.6 Assistance with GDPR Compliance (Article 28(3)(f))</h3>
      <p style={s.p}>
        We will assist you in ensuring compliance with Articles 32-36 of the GDPR, taking into account the nature of processing and information available to us. This includes:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Article 32 (Security):</strong> Providing information about our security measures (Section 4.3 above)</li>
        <li style={s.li}><strong>Article 33/34 (Breach notification):</strong> See Section 6 below</li>
        <li style={s.li}><strong>Article 35 (DPIA):</strong> Providing information about our processing activities, data flows, and security measures to support your Data Protection Impact Assessments</li>
        <li style={s.li}><strong>Article 36 (Prior consultation):</strong> Assisting with prior consultation of a supervisory authority where required</li>
      </ul>

      <h3 style={s.h3}>4.7 Deletion or Return of Data (Article 28(3)(g))</h3>
      <p style={s.p}>Upon termination of the Service:</p>
      <ul style={s.ul}>
        <li style={s.li}>You may export your data during the <strong>30-day grace period</strong> following account cancellation.</li>
        <li style={s.li}>After 30 days, we will <strong>permanently delete</strong> all Personal Data using the following cascade:</li>
      </ul>
      <ol style={{ ...s.ul, listStyleType: 'decimal', paddingLeft: 48 }}>
        <li style={s.li}>Email event records</li>
        <li style={s.li}>Email metadata</li>
        <li style={s.li}>Enforcement logs</li>
        <li style={s.li}>Suppression lists</li>
        <li style={s.li}>DKIM keys (also revoked)</li>
        <li style={s.li}>Domain records</li>
        <li style={s.li}>Webhook configurations</li>
        <li style={s.li}>API keys</li>
        <li style={s.li}>R2 objects (templates, orphaned attachments)</li>
        <li style={s.li}>Account record (anonymized: email hashed, name cleared)</li>
        <li style={s.li}>KV cache entries</li>
      </ol>
      <ul style={s.ul}>
        <li style={s.li}><strong>Exception:</strong> Billing records are retained as required by tax and accounting law (approximately 7 years). These do not contain email recipient data.</li>
        <li style={s.li}>Written confirmation of deletion provided on request.</li>
      </ul>

      <h3 style={s.h3}>4.8 Audit and Inspection (Article 28(3)(h))</h3>
      <p style={s.p}>
        We will make available to you all information necessary to demonstrate compliance with our Article 28 obligations.
      </p>
      <p style={s.p}>
        We will allow and contribute to audits, including inspections, conducted by you or an auditor you mandate, subject to:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Reasonable notice:</strong> 30 days written notice</li>
        <li style={s.li}><strong>Frequency:</strong> Limited to once per year, unless a data breach has occurred</li>
        <li style={s.li}><strong>Cost:</strong> At your expense</li>
        <li style={s.li}><strong>Scope:</strong> Limited to processing activities covered by this DPA</li>
        <li style={s.li}><strong>Confidentiality:</strong> Auditor must be bound by confidentiality obligations</li>
      </ul>
      <p style={s.p}>
        We may satisfy audit requirements by providing SOC 2 reports, ISO certifications, or equivalent third-party audit reports if and when obtained.
      </p>

      {/* 5. Data Location and International Transfers */}
      <h2 id="location" style={s.h2}>5. Data Location and International Transfers</h2>

      <h3 style={s.h3}>Primary Data Storage (EU)</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Component</th>
              <th style={thStyle}>Provider</th>
              <th style={thStyle}>Location</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Database (Postgres)</td><td style={tdStyle}>Neon</td><td style={tdStyle}>EU region</td></tr>
            <tr><td style={tdStyle}>SMTP infrastructure</td><td style={tdStyle}>Scaleway</td><td style={tdStyle}>Paris, France (EU)</td></tr>
            <tr><td style={tdStyle}>Temporary storage (R2)</td><td style={tdStyle}>Cloudflare</td><td style={tdStyle}>EU</td></tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>International Transfer Mechanisms</h3>
      <p style={s.p}>Personal Data may transit through non-EU infrastructure in the following scenarios:</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Scenario</th>
              <th style={thStyle}>Mechanism</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>API requests processed at Cloudflare&apos;s global edge</td>
              <td style={tdStyle}>Cloudflare&apos;s DPA includes EU SCCs; Cloudflare is certified under the EU-US Data Privacy Framework</td>
            </tr>
            <tr>
              <td style={tdStyle}>Billing data processed by Polar</td>
              <td style={tdStyle}>Per Polar&apos;s data protection practices</td>
            </tr>
            <tr>
              <td style={tdStyle}>DNS management via Vercel (auto-configure only)</td>
              <td style={tdStyle}>Only when you explicitly authorize via OAuth; Vercel participates in the EU-US Data Privacy Framework</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        <strong>Standard Contractual Clauses:</strong> This DPA incorporates the EU Commission&apos;s Standard Contractual Clauses (Module 2: Controller to Processor, and Module 3: Processor to Processor) as referenced in the Annex. These SCCs apply to any transfer of Personal Data outside the EU/EEA.
      </p>
      <p style={s.p}>
        <strong>UK Addendum:</strong> For transfers of UK Personal Data, this DPA incorporates the UK International Data Transfer Addendum to the EU Commission Standard Contractual Clauses, as issued by the UK Information Commissioner under S119A(1) of the Data Protection Act 2018 (the &ldquo;UK Addendum&rdquo;). The UK Addendum is deemed completed as set out in Annex 4 below.
      </p>
      <p style={s.p}>
        <strong>Brazilian Transfers:</strong> For transfers of Personal Data subject to Brazil&apos;s LGPD, One-Email will comply with applicable ANPD requirements for international data transfers. As our primary infrastructure is EU-based, transfers are covered by the mechanisms described above. If ANPD-specific Standard Contractual Clauses become required for your use case, we will work with you to put them in place.
      </p>

      {/* 6. Data Breach Notification */}
      <h2 id="breach" style={s.h2}>6. Data Breach Notification</h2>
      <p style={s.p}>In the event of a Personal Data breach, we will:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Notify you without undue delay</strong>, and no later than <strong>48 hours</strong> after becoming aware of the breach</li>
        <li style={s.li}>Provide the following information:</li>
      </ul>
      <ul style={{ ...s.ul, paddingLeft: 48 }}>
        <li style={s.li}>Nature of the breach (what happened)</li>
        <li style={s.li}>Categories and approximate number of data subjects affected</li>
        <li style={s.li}>Categories and approximate number of Personal Data records affected</li>
        <li style={s.li}>Name and contact details of our privacy contact</li>
        <li style={s.li}>Likely consequences of the breach</li>
        <li style={s.li}>Measures taken or proposed to address the breach and mitigate effects</li>
      </ul>
      <ul style={s.ul}>
        <li style={s.li}><strong>Cooperate with you</strong> in notifying the relevant supervisory authority and affected data subjects, as required by GDPR Articles 33 and 34</li>
        <li style={s.li}><strong>Document</strong> all breaches regardless of whether they are reportable</li>
      </ul>
      <p style={s.p}>
        Our 48-hour commitment exceeds the GDPR&apos;s &ldquo;without undue delay&rdquo; standard, ensuring you have time to meet your own 72-hour obligation to notify your supervisory authority.
      </p>

      {/* 7. CCPA/CPRA Service Provider Terms */}
      <h2 id="ccpa" style={s.h2}>7. CCPA/CPRA Service Provider Terms</h2>
      <p style={s.p}>
        Where you are a &ldquo;business&rdquo; under the California Consumer Privacy Act (CCPA/CPRA) and One-Email processes Personal Data of California residents on your behalf:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>One-Email acts as a <strong>&ldquo;service provider&rdquo;</strong> as defined in Cal. Civ. Code 1798.140(ag).</li>
        <li style={s.li}>We will <strong>not</strong> retain, use, or disclose Personal Data for any purpose other than performing the Service.</li>
        <li style={s.li}>We will <strong>not</strong> sell or share Personal Data.</li>
        <li style={s.li}>We will <strong>not</strong> combine Personal Data received from you with data from other sources, except as permitted for a service provider.</li>
        <li style={s.li}>We will assist you in responding to consumer rights requests (access, deletion, opt-out) as required by CCPA.</li>
        <li style={s.li}>We certify that we understand and will comply with these CCPA service provider obligations.</li>
      </ul>

      {/* 8. Term and Termination */}
      <h2 id="term" style={s.h2}>8. Term and Termination</h2>
      <ul style={s.ul}>
        <li style={s.li}>This DPA takes effect when you accept the Terms of Service and remains in effect for the duration of your use of the Service.</li>
        <li style={s.li}>This DPA <strong>survives termination</strong> of the Terms of Service until all Personal Data has been deleted or returned per Section 4.7.</li>
        <li style={s.li}>Either party may terminate this DPA if the other party materially breaches it and fails to cure within 30 days of written notice.</li>
      </ul>

      {/* 9. Liability */}
      <h2 id="liability" style={s.h2}>9. Liability</h2>
      <p style={s.p}>
        Liability under this DPA is subject to the limitations set out in the <a href="/legal/terms">Terms of Service</a>, except where limitation is prohibited by Applicable Data Protection Law.
      </p>

      {/* 10. Contact */}
      <h2 id="contact" style={s.h2}>10. Contact</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Privacy inquiries:</strong> privacy@one-email.com</li>
        <li style={s.li}><strong>Breach notifications:</strong> security@one-email.com</li>
        <li style={s.li}><strong>Legal entity:</strong> IMP Capital AB, Rotebrunnsv&auml;gen 3, 393 55 Kalmar, Sweden</li>
      </ul>

      {/* Annex 1 */}
      <h2 id="annex-1" style={s.h2}>Annex 1: Processing Details (Article 28(3))</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Field</th>
              <th style={thStyle}>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}><strong>Subject matter</strong></td><td style={tdStyle}>Email delivery infrastructure services</td></tr>
            <tr><td style={tdStyle}><strong>Duration</strong></td><td style={tdStyle}>Duration of service agreement + 30-day post-termination grace period</td></tr>
            <tr><td style={tdStyle}><strong>Nature of processing</strong></td><td style={tdStyle}>Collection, storage, transmission, deletion of email metadata; transmission of email content (not stored)</td></tr>
            <tr><td style={tdStyle}><strong>Purpose</strong></td><td style={tdStyle}>Delivering emails on the Controller&apos;s behalf, providing delivery analytics and status tracking</td></tr>
            <tr><td style={tdStyle}><strong>Types of Personal Data</strong></td><td style={tdStyle}>Email addresses (sender/recipient), subject line hash, delivery timestamps, delivery status, sending IP, customer-defined tags, SMTP message ID</td></tr>
            <tr><td style={tdStyle}><strong>Categories of Data Subjects</strong></td><td style={tdStyle}>Recipients of emails sent by the Controller through One-Email</td></tr>
          </tbody>
        </table>
      </div>

      {/* Annex 2 */}
      <h2 id="annex-2" style={s.h2}>Annex 2: Security Measures (Article 32)</h2>
      <p style={s.p}>See Section 4.3 of this DPA.</p>

      {/* Annex 3 */}
      <h2 id="annex-3" style={s.h2}>Annex 3: Sub-processors</h2>
      <p style={s.p}>
        See <a href="/legal/subprocessors">/legal/subprocessors</a> for the current list.
      </p>

      {/* Annex 4 */}
      <h2 id="annex-4" style={s.h2}>Annex 4: Standard Contractual Clauses &amp; UK Addendum</h2>

      <h3 style={s.h3}>EU Standard Contractual Clauses</h3>
      <p style={s.p}>
        This DPA incorporates by reference the Standard Contractual Clauses approved by the European Commission in Implementing Decision (EU) 2021/914 of 4 June 2021 (the &ldquo;EU SCCs&rdquo;), available at{' '}
        <a href="https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj" target="_blank" rel="noopener noreferrer">eur-lex.europa.eu</a>.
      </p>
      <p style={s.p}>The EU SCCs are deemed completed as follows:</p>
      <p style={s.p}>
        <strong>Module 2 (Controller to Processor)</strong> applies where you (Controller) transfer Personal Data to One-Email (Processor).
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Clause</th>
              <th style={thStyle}>Selection</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Clause 7 (Docking clause)</td><td style={tdStyle}>Included &mdash; third parties may accede to these clauses</td></tr>
            <tr><td style={tdStyle}>Clause 9(a) (Sub-processor authorization)</td><td style={tdStyle}>Option 2: General written authorization, with 30 days prior notice of changes</td></tr>
            <tr><td style={tdStyle}>Clause 11(a) (Redress)</td><td style={tdStyle}>The optional language is NOT included</td></tr>
            <tr><td style={tdStyle}>Clause 13(a) (Supervision)</td><td style={tdStyle}>The supervisory authority of the EU Member State in which the data exporter is established shall act as the competent supervisory authority. Where the data exporter is not established in the EU: Integritetsskyddsmyndigheten (IMY), Sweden.</td></tr>
            <tr><td style={tdStyle}>Clause 17 (Governing law)</td><td style={tdStyle}>Option 1: The law of Sweden</td></tr>
            <tr><td style={tdStyle}>Clause 18(b) (Forum)</td><td style={tdStyle}>The courts of Sweden</td></tr>
            <tr><td style={tdStyle}>Annex I.A (List of parties)</td><td style={tdStyle}>Data exporter: You (the customer). Data importer: IMP Capital AB, Rotebrunnsv&auml;gen 3, 393 55 Kalmar, Sweden. Contact: privacy@one-email.com</td></tr>
            <tr><td style={tdStyle}>Annex I.B (Description of transfer)</td><td style={tdStyle}>As set out in Annex 1 of this DPA (Processing Details)</td></tr>
            <tr><td style={tdStyle}>Annex I.C (Competent supervisory authority)</td><td style={tdStyle}>IMY (Sweden), or the supervisory authority of the data exporter&apos;s EU establishment</td></tr>
            <tr><td style={tdStyle}>Annex II (Technical and organizational measures)</td><td style={tdStyle}>As set out in Section 4.3 of this DPA (Security Measures)</td></tr>
            <tr><td style={tdStyle}>Annex III (List of sub-processors)</td><td style={tdStyle}>As set out at <a href="/legal/subprocessors">/legal/subprocessors</a></td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        <strong>Module 3 (Processor to Processor)</strong> applies where One-Email (Processor) transfers Personal Data to a sub-processor (e.g., Cloudflare for edge processing). The same selections above apply, with One-Email as the data exporter and the sub-processor as the data importer.
      </p>

      <h3 style={s.h3}>UK International Data Transfer Addendum</h3>
      <p style={s.p}>
        For transfers of UK Personal Data, the UK Addendum (as approved by the UK Information Commissioner under S119A(1) Data Protection Act 2018, version B1.0 in force 21 March 2022) is incorporated by reference and deemed completed as follows:
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Table</th>
              <th style={thStyle}>Entry</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Table 1 (Parties)</td><td style={tdStyle}>Data exporter: You (the customer). Data importer: IMP Capital AB.</td></tr>
            <tr><td style={tdStyle}>Table 2 (Selected SCCs)</td><td style={tdStyle}>The EU SCCs as set out above (Module 2 and Module 3), including the Annex information</td></tr>
            <tr><td style={tdStyle}>Table 3 (Appendix information)</td><td style={tdStyle}>As per the EU SCCs Annexes above</td></tr>
            <tr><td style={tdStyle}>Table 4 (Ending the Addendum)</td><td style={tdStyle}>Neither party may end the UK Addendum per Section 19 of the UK Addendum</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        The UK Addendum is available at{' '}
        <a href="https://ico.org.uk/for-organisations/guide-to-data-protection/guide-to-the-general-data-protection-regulation-gdpr/international-data-transfer-agreement-and-guidance/" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
        In the event of conflict between the UK Addendum and the EU SCCs, the UK Addendum prevails for UK transfers.
      </p>
    </div>
  )
}
